/**
 * @file     useVowRegistrationResume.ts
 * @purpose  Reopens the VOW access modal for a signed-in user whose
 *           registration was interrupted by email confirmation. The phone they
 *           already typed is parked in localStorage by VowAccessModal; when
 *           they return authenticated but still gated, the modal resumes at
 *           the phone/terms step instead of leaving sold data silently grey.
 */
'use client';

import { useEffect, useRef } from 'react';

/** Shared with VowAccessModal, which writes it on the check-email hand-off and
 *  clears it after a completed registration. */
export const VOW_PENDING_PHONE_KEY = 'relta_vow_pending_phone';

export function readVowPendingPhone(): string {
  try {
    return window.localStorage.getItem(VOW_PENDING_PHONE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function useVowRegistrationResume({
  authed,
  locked,
  onResume,
}: {
  authed: boolean;
  locked: boolean;
  onResume: () => void;
}) {
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current || !authed || !locked) return;
    if (!readVowPendingPhone()) return;
    fired.current = true;
    onResume();
    // onResume is a state setter from the mounting surface; identity churn on
    // it must not re-trigger the resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, locked]);
}
